import { EmailRecipient } from 'components/input/EmailInput/types';
import { SuggestedRecipientEmail } from 'lib/api/scheduling/getSuggestedRecipients';
import { normalizeEmail } from 'utils/emails';

export type BuildSchedulingRecipientsInput = {
  tmsEmail: string;
  tmsContactName: string;
  learnedEmails: SuggestedRecipientEmail[];
};

export type BuildSchedulingRecipientsResult = {
  // Prefill values for the To field.
  initialRecipients: EmailRecipient[];
  // Suggestion chips / dropdown options (includes prefilled emails).
  suggestedRecipients: EmailRecipient[];
};

// Merges TMS stop email with learned facility associations for Email Request.
// - TMS email wins for prefill when present; learned emails are suggestions only.
// - When TMS is empty, top learned email (by usage) is prefilled; rest are suggestions.
export function buildSchedulingRecipients({
  tmsEmail,
  tmsContactName,
  learnedEmails,
}: BuildSchedulingRecipientsInput): BuildSchedulingRecipientsResult {
  const learned = learnedEmails
    .map((row) => ({
      email: normalizeEmail(row.email),
      usageCount: row.usageCount,
    }))
    .filter((row) => row.email !== '');

  const seen = new Set<string>();
  const uniqueLearned: SuggestedRecipientEmail[] = [];
  for (const row of learned) {
    if (seen.has(row.email)) continue;
    seen.add(row.email);
    uniqueLearned.push(row);
  }
  uniqueLearned.sort((a, b) => b.usageCount - a.usageCount);

  const tmsNormalized = normalizeEmail(tmsEmail);
  const suggestedRecipients: EmailRecipient[] = uniqueLearned.map((row) => ({
    email: row.email,
    name: row.email,
  }));

  if (tmsNormalized) {
    const initialRecipients: EmailRecipient[] = [
      { email: tmsNormalized, name: tmsContactName || tmsNormalized },
    ];

    // Include TMS in suggestion options so the AI hint still shows, and keep
    // other learned emails available for quick add.
    if (!uniqueLearned.some((row) => row.email === tmsNormalized)) {
      suggestedRecipients.unshift(initialRecipients[0]);
    }

    return { initialRecipients, suggestedRecipients };
  }

  if (uniqueLearned.length === 0) {
    return { initialRecipients: [], suggestedRecipients: [] };
  }

  const top = uniqueLearned[0];
  return {
    initialRecipients: [{ email: top.email, name: top.email }],
    suggestedRecipients,
  };
}
